import { router } from '@inertiajs/react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import BasesContent, { isBasesEdition } from '@/components/bases/bases-content';
import { Button } from '@/components/ui/button';
import type { OnboardingEdition } from '@/types';
import { OnboardingShell, titleFont } from './OnboardingShell';

// _components/terms-step.tsx. The bases document is the edition's (PORTING.md G5); the
// button unlocks once the container has been scrolled to the bottom.
export function TermsStep({ edition }: { edition: OnboardingEdition }) {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const scrolledToBottom = scrollTop + clientHeight >= scrollHeight - 10;

      if (scrolledToBottom && !hasScrolledToBottom) {
        setHasScrolledToBottom(true);
      }
    };

    container.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => container.removeEventListener('scroll', handleScroll);
  }, [hasScrolledToBottom]);

  const handleAccept = () => {
    setIsAccepting(true);
    router.post(
      `${edition.basePath}/terms`,
      {},
      {
        onSuccess: () => toast.success('Bases aceptadas correctamente'),
        onError: () => {
          toast.error('Error al aceptar las bases');
          setIsAccepting(false);
        },
      },
    );
  };

  return (
    <OnboardingShell ph26={edition.ph26} width="max-w-4xl">
      {/* Title */}
      <div className="text-center">
        <h1
          className={`inline-block font-bold ${titleFont(edition.ph26)} text-2xl text-primary sm:text-3xl md:text-4xl`}
        >
          Aceptar{' '}
          <span className="bg-primary px-2 py-1 text-background">Bases</span>
        </h1>
        <p className="mt-4 font-mono text-primary/70 text-sm">
          Lee y acepta las bases de participación del evento
        </p>
      </div>

      {/* Terms Container */}
      <div className="border-2 border-primary bg-background/80 backdrop-blur-sm">
        <div
          ref={containerRef}
          className="max-h-[60vh] overflow-y-auto p-6 sm:p-8 md:p-10"
        >
          {isBasesEdition(edition.editionSlug) && (
            <BasesContent edition={edition.editionSlug} />
          )}
        </div>

        {/* Accept Button */}
        <div className="border-primary border-t-2 p-6 sm:p-8">
          <div className="flex justify-center">
            <Button
              onClick={handleAccept}
              disabled={!hasScrolledToBottom || isAccepting}
              size="lg"
              className="w-full sm:w-auto"
            >
              {isAccepting ? 'Aceptando...' : 'Aceptar y Continuar'}
            </Button>
          </div>
        </div>
      </div>
    </OnboardingShell>
  );
}
